import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import { membershipEndpoints } from '@/lib/api/endpoints';
import { membershipKeys } from '@/lib/queryKeys';
import type { PlanRecurringServiceInput } from '@/hooks/membership-plans/usePlanRecurringServices';

/** A snapshotted recurring service on a specific customer's membership. */
export interface MembershipRecurringServiceInstance {
  id: string;
  membershipId: string;
  label: string | null;
  jobTypeId: string;
  businessUnitId: string | null;
  invoiceTemplateItemId: string | null;
  priority: string | null;
  jobSummary: string | null;
  recurrenceType: 'WEEKLY' | 'MONTHLY' | 'SEASONAL' | 'DAILY';
  recurrenceInterval: number;
  recurrenceMonths: number[];
  durationType: 'FIXED' | 'CONTINUOUS';
  totalVisits: number | null;
  firstVisitComplete: boolean;
  startDate: string;
  visitsRemaining: number | null;
  status: string;
  coverageId: string | null;
  attachTo: 'PROPERTY' | 'UNIT';
  scaling: 'EXTEND' | 'MULTIPLY';
  extraUnitMinutes: number;
  coveredEquipmentIds: string[];
}

export type CreateMembershipRecurringServiceInput = PlanRecurringServiceInput & {
  startDate?: string;
};

type Envelope<T> = { data: T };

const key = (membershipId: string) => ['membership-recurring-services', membershipId] as const;

/** The recurring service instances attached to one membership. */
export function useMembershipRecurringServices(membershipId: string | null) {
  return useApiQuery<MembershipRecurringServiceInstance[]>({
    queryKey: key(membershipId ?? 'none'),
    enabled: Boolean(membershipId),
    queryFn: async () => {
      const res = await apiGet<Envelope<MembershipRecurringServiceInstance[]>>(
        operationsClient,
        membershipEndpoints.recurringServices(membershipId!),
      );
      return res.data;
    },
  });
}

/** Editing the schedule resyncs pending visits server-side, so invalidate the
 * membership detail and the office "Included visits" view too. */
function invalidateAfterMutation(
  queryClient: ReturnType<typeof useQueryClient>,
  membershipId: string,
) {
  void queryClient.invalidateQueries({ queryKey: key(membershipId) });
  void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membershipId) });
  void queryClient.invalidateQueries({ queryKey: ['recurring-service-events'] });
}

export function useCreateMembershipRecurringService(membershipId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    MembershipRecurringServiceInstance[],
    CreateMembershipRecurringServiceInput
  >({
    mutationFn: async (input) => {
      const res = await apiPost<
        Envelope<MembershipRecurringServiceInstance[]>,
        CreateMembershipRecurringServiceInput
      >(operationsClient, membershipEndpoints.recurringServices(membershipId), input);
      return res.data;
    },
    onSuccess: () => invalidateAfterMutation(queryClient, membershipId),
  });
}

export function useUpdateMembershipRecurringService(membershipId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    MembershipRecurringServiceInstance,
    { id: string; input: PlanRecurringServiceInput }
  >({
    mutationFn: async ({ id, input }) => {
      const res = await apiPatch<
        Envelope<MembershipRecurringServiceInstance>,
        PlanRecurringServiceInput
      >(operationsClient, membershipEndpoints.recurringService(membershipId, id), input);
      return res.data;
    },
    onSuccess: () => invalidateAfterMutation(queryClient, membershipId),
  });
}

export function useDeleteMembershipRecurringService(membershipId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<{ deleted: boolean }, string>({
    mutationFn: (id) =>
      apiDelete<{ deleted: boolean }>(
        operationsClient,
        membershipEndpoints.recurringService(membershipId, id),
      ),
    onSuccess: () => invalidateAfterMutation(queryClient, membershipId),
  });
}
